import React from 'react';
import { hydrateRoot, createRoot } from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

// Global error handler for React error #31
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('🚨 REACT ERROR BOUNDARY CAUGHT:', error);
    console.error('🚨 ERROR INFO:', errorInfo);
    
    // Handle ChunkLoadError - reload page to get fresh chunks
    if (error && (
      error.message?.includes('Loading chunk') ||
      error.message?.includes('ChunkLoadError') ||
      error.name === 'ChunkLoadError' ||
      error.message?.includes('Failed to fetch dynamically imported module')
    )) {
      console.warn('⚠️ ChunkLoadError detected in ErrorBoundary - reloading page...');
      // Clear cache and reload
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => caches.delete(name));
        });
      }
      setTimeout(() => window.location.reload(), 100);
      return;
    }
    
    // Safely access errorInfo properties
    if (errorInfo && errorInfo.componentStack) {
      console.error('🚨 COMPONENT STACK:', errorInfo.componentStack);
    } else {
      console.error('🚨 COMPONENT STACK: Not available');
    }
    
    // Check if this is React error #31
    if (error && error.message && error.message.includes('Objects are not valid as a React child')) {
      console.error('🚨 REACT ERROR #31 DETECTED!');
      console.error('🚨 This error occurs when trying to render objects directly in JSX');
      console.error('🚨 Look for patterns like: {someObject} instead of {someObject.property}');
    }
    
    this.setState({
      error,
      errorInfo
    });
  }

  render() {
    if (this.state.hasError) {
      const isChunkError = this.state.error && (
        this.state.error.message?.includes('Loading chunk') ||
        this.state.error.message?.includes('ChunkLoadError') ||
        this.state.error.name === 'ChunkLoadError' ||
        this.state.error.message?.includes('Failed to fetch dynamically imported module')
      );

      if (isChunkError) {
        return (
          <div style={{ padding: '40px', backgroundColor: '#fff3cd', border: '2px solid #ffc107', textAlign: 'center', maxWidth: '600px', margin: '50px auto', borderRadius: '8px' }}>
            <h1 style={{ color: '#856404', marginBottom: '20px' }}>🔄 Updating Application</h1>
            <p style={{ color: '#856404', fontSize: '16px', marginBottom: '20px' }}>
              A new version of the application is available. The page will reload automatically...
            </p>
            <button 
              onClick={() => {
                if ('caches' in window) {
                  caches.keys().then(names => {
                    names.forEach(name => caches.delete(name));
                  });
                }
                window.location.reload();
              }} 
              style={{ padding: '12px 24px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '16px' }}
            >
              Reload Now
            </button>
          </div>
        );
      }

      return (
        <div style={{ padding: '20px', backgroundColor: '#fee', border: '2px solid red' }}>
          <h1>🚨 Application Error Detected</h1>
          <h2>Error: {this.state.error && this.state.error.toString()}</h2>
          <details style={{ whiteSpace: 'pre-wrap', marginTop: '10px' }}>
            <summary>Click for error details</summary>
            {this.state.error && this.state.error.stack}
            <br />
            Component Stack:
            {this.state.errorInfo && this.state.errorInfo.componentStack ? this.state.errorInfo.componentStack : 'Not available'}
          </details>
          <button 
            onClick={() => window.location.reload()} 
            style={{ marginTop: '10px', padding: '10px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px' }}
          >
            Reload Application
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Override console.error to catch React error #31
const originalConsoleError = console.error;
console.error = (...args) => {
  const message = args.join(' ');
  
  if (message.includes('Objects are not valid as a React child') || 
      message.includes('Error #31') ||
      message.includes('minified React error #31')) {
    console.error('🚨🚨🚨 REACT ERROR #31 DETECTED IN CONSOLE 🚨🚨🚨');
    console.error('🚨 FULL ERROR MESSAGE:', message);
    console.error('🚨 STACK TRACE:', new Error().stack);
    
    // Try to extract component information
    args.forEach((arg, index) => {
      console.error(`🚨 ARG ${index}:`, arg);
    });
  }
  
  originalConsoleError.apply(console, args);
};

// Add global error handler for unhandled errors
window.addEventListener('error', (event) => {
  console.error('🚨 GLOBAL ERROR:', event.error);
  console.error('🚨 ERROR MESSAGE:', event.message);
  console.error('🚨 ERROR FILENAME:', event.filename);
  console.error('🚨 ERROR LINE:', event.lineno);
  console.error('🚨 ERROR COLUMN:', event.colno);
});

// Add global handler for unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  console.error('🚨 UNHANDLED PROMISE REJECTION:', event.reason);
  
  // Handle ChunkLoadError - reload page to get fresh chunks
  if (event.reason && (
    event.reason.message?.includes('Loading chunk') ||
    event.reason.message?.includes('ChunkLoadError') ||
    event.reason.name === 'ChunkLoadError'
  )) {
    console.warn('⚠️ ChunkLoadError detected - reloading page to fetch fresh chunks...');
    // Clear cache and reload
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    window.location.reload();
  }
});

// Handle chunk load errors specifically
window.addEventListener('error', (event) => {
  // Check if it's a chunk load error
  if (event.message && (
    event.message.includes('Loading chunk') ||
    event.message.includes('ChunkLoadError') ||
    event.message.includes('Failed to fetch dynamically imported module')
  )) {
    console.warn('⚠️ ChunkLoadError detected - reloading page to fetch fresh chunks...');
    // Clear cache and reload
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    window.location.reload();
    return true; // Prevent default error handling
  }
}, true);

// react-snap saved the demo popup's scroll lock onto <body>. Clear it before
// React mounts so every route can scroll, including pages that never show the popup.
if (document.body && document.body.style.overflow === 'hidden') {
  document.body.style.overflow = '';
}

let rootEl = document.getElementById('root');
if (!rootEl) {
  rootEl = document.createElement('div');
  rootEl.id = 'root';
  document.body.appendChild(rootEl);
}
const app = (
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);

if (rootEl.hasChildNodes()) {
  hydrateRoot(rootEl, app);
} else {
  createRoot(rootEl).render(app);
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
